import { ErrorResponse } from '@opositandopn/http';
import { Variant, useToasterStore } from '@opositandopn/ui';
import { isAxiosError } from 'axios';

export type ErrorMessage = {
  title: string;
  message?: string;
  variant?: Variant;
};

export const FALLBACK_ERROR_CODE = '*';

const spokenFor = new WeakSet<object>();

/**
 * Marks a failure as already accounted for, so `notifyError` stays quiet and
 * the two layers never speak over each other. Either the axios interceptor
 * reported it (a session-level event only it can resolve) or the request asked
 * to stay `silent`. Set by the code that makes that call, so it cannot drift
 * out of step with it.
 */
export function markNotified(error: unknown) {
  if (error !== null && typeof error === 'object') {
    spokenFor.add(error);
  }
}

function isNotified(error: unknown): boolean {
  return error !== null && typeof error === 'object' && spokenFor.has(error);
}

const GENERIC_ERROR: ErrorMessage & { message: string } = {
  title: '¡Ups!',
  message:
    'Parece que ha ocurrido un error. Por favor, inténtalo de nuevo más tarde.',
};

/**
 * A read that failed with a 4xx has nothing to tell the user: the resource is
 * not there or not theirs, which the component renders as an empty state. The
 * API answers 404 for "you have no response to this survey yet" and the like,
 * so toasting these would speak over ordinary flows.
 */
export function isClientError(error: unknown): boolean {
  const status = isAxiosError(error) ? error.response?.status : undefined;

  return status !== undefined && status >= 400 && status < 500;
}

function lookup(
  messages: Record<string, ErrorMessage> | undefined,
  code: string | undefined,
): ErrorMessage | undefined {
  return messages !== undefined &&
    code !== undefined &&
    Object.hasOwn(messages, code)
    ? messages[code]
    : undefined;
}

export function notifyError(
  error: unknown,
  messages?: Record<string, ErrorMessage>,
) {
  if (isNotified(error)) {
    return;
  }

  const data = isAxiosError<ErrorResponse>(error)
    ? error.response?.data
    : undefined;
  const copy =
    lookup(messages, data?.code) ??
    lookup(messages, FALLBACK_ERROR_CODE) ??
    GENERIC_ERROR;

  useToasterStore
    .getState()
    .actions.sendMessage(
      copy.title,
      copy.message ?? data?.message ?? GENERIC_ERROR.message,
      copy.variant ?? 'danger',
    );
}
